import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material';
import { ReactNode } from 'react';

// Blue color for the entire application

interface CardProps {
  icon: ReactNode;
  title: string;
  children?: ReactNode;
  description?: string;
  frame?: string;
  frameLink?: string;
}

const ResourceCard: React.FC<CardProps> = ({
  icon,
  title,
  children,
  description,
  frame,
  frameLink,
}) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        borderRadius: '8px',
        padding: '1.2rem',
        boxShadow:
          '0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06)',
        backgroundColor: theme.palette.background.paper,
        width: '100%',
        height: '100%',
      }}
      data-testid={`exp-resources-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <Box
        sx={{
          backgroundColor: 'rgba(229, 231, 255, 0.7)',
          borderRadius: '50%',
          width: '70px',
          height: '70px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '1.5rem',
        }}
        data-testid={`exp-resources-${title.toLowerCase().replace(/\s+/g, '-')}-icon`}
      >
        {icon}
      </Box>

      <Typography
        sx={{
          width: '100%',
          textAlign: 'center',
          color: theme.palette.text.primary,
          fontWeight: 600,
          fontSize: '1.25rem',
          marginBottom: '0.3rem',
        }}
        variant="h5"
        data-testid={`exp-resources-${title.toLowerCase().replace(/\s+/g, '-')}-title`}
      >
        {title}
      </Typography>

      {description && (
        <Typography
          sx={{
            width: '100%',
            textAlign: 'center',
            color: theme.palette.text.secondary,
            marginBottom: '1rem',
            fontSize: '1rem',
            lineHeight: 1.5,
          }}
          variant="body1"
          data-testid={`exp-resources-${title.toLowerCase().replace(/\s+/g, '-')}-description`}
        >
          {description}
        </Typography>
      )}

      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
        }}
        data-testid={`exp-resources-${title.toLowerCase().replace(/\s+/g, '-')}-children`}
      >
        {children}
      </Box>
      {frame && (
        <Box
          data-testid="frame-section"
          sx={{
            marginTop: '1.5rem',
            width: '100%',
            textAlign: 'center',
          }}
        >
          {frameLink ? (
            <Typography
              component="a"
              href={frameLink}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: theme.palette.primary.main,
                fontWeight: 500,
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline',
                },
                fontSize: '0.95rem',
                cursor: 'pointer',
              }}
              data-testid={`exp-resources-${title
                .toLowerCase()
                .replace(/\s+/g, '-')}-frame-link`}
            >
              {frame}
            </Typography>
          ) : (
            <Typography
              data-testid="frame-text"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: '0.95rem',
              }}
            >
              {frame}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ResourceCard;
